import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import ServicesPanel from "../components/admin/ServicesPanel";
import PortfolioPanel from "../components/admin/PortfolioPanel";
import ReviewsPanel from "../components/admin/ReviewsPanel";
import UsersPanel from "../components/admin/UsersPanel";
import OrdersPanel from "../components/admin/OrdersPanel";
import CalendarPanel from "../components/admin/CalendarPanel";
import RatesPanel from "../components/admin/RatesPanel";
import SettingsPanel from "../components/admin/SettingsPanel";
import {
  LogOut, Package, Image as ImgIcon, Star, Users, ClipboardList, Calendar as CalIcon,
  BarChart3, Search, Shield, FileText, Coins, Settings as SettingsIcon
} from "lucide-react";

const TABS = [
  { id: "services", label: "📦 Услуги", icon: Package, min: 7 },
  { id: "portfolio", label: "📁 Портфолио", icon: ImgIcon, min: 7 },
  { id: "reviews", label: "⭐ Отзывы", icon: Star, min: 3 },
  { id: "users", label: "👥 Пользователи", icon: Users, min: 3 },
  { id: "orders", label: "📋 Заказы", icon: ClipboardList, min: 3 },
  { id: "calendar", label: "📅 Календарь", icon: CalIcon, min: 7 },
  { id: "market", label: "📊 Рынок", icon: BarChart3, min: 7 },
  { id: "search", label: "🔍 Поиск", icon: Search, min: 7 },
  { id: "roles", label: "🛡️ Роли", icon: Shield, min: 10 },
  { id: "logs", label: "📋 Логи", icon: FileText, min: 7 },
  { id: "rates", label: "💱 Курсы", icon: Coins, min: 7 },
  { id: "settings", label: "⚙️ Настройки", icon: SettingsIcon, min: 10 },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("services");
  const [stats, setStats] = useState(null);

  useEffect(() => { if (user === false) nav("/admin/login"); }, [user, nav]);
  useEffect(() => {
    if (!user) return;
    (async () => {
      try { const { data } = await api.get("/admin/stats"); setStats(data); }
      catch (e) { /* ignore if forbidden */ }
    })();
  }, [user, tab]);

  if (!user) return <div className="min-h-screen flex items-center justify-center text-gray-400">Загрузка…</div>;

  const canView = (min) => (user.role_level || 0) >= min;
  const visible = TABS.filter((t) => canView(t.min));

  return (
    <div className="min-h-screen grid-noise">
      <header className="border-b border-white/5 sticky top-0 z-20 backdrop-blur-xl" style={{background: "rgba(10,10,10,0.75)"}}>
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="text-gold text-xl">◆</span>
            <span className="font-heading text-xl font-bold">ARVEX</span>
            <span className="chip">DASHBOARD</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-medium">👑 {user.name || user.email}</div>
              <div className="text-xs text-gold uppercase tracking-widest">{user.role} · lvl {user.role_level}</div>
            </div>
            <button onClick={async () => { await logout(); nav("/admin/login"); }} className="btn-outline !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid="dashboard-logout-btn">
              <LogOut size={14}/> 🚪 Выйти
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-8">
        {stats && (
          <div className="mb-8 fade-up">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">
              👑 Добро пожаловать, <span className="text-gold gold-glow">Артём Альбертович!</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 mt-6">
              {[
                { v: stats.services, l: "Услуг" },
                { v: stats.portfolio, l: "Портфолио" },
                { v: stats.reviews, l: `Отзывов · ${stats.reviews_pending} на пров.` },
                { v: stats.users, l: "Пользователей" },
                { v: stats.orders, l: `Заказов · ${stats.orders_new} нов.` },
                { v: stats.deadlines_week, l: `Дедлайнов на неделе · ${stats.orders_overdue} проср.` },
                { v: stats.ai_price_suggestions, l: "AI-предложений по ценам" },
                { v: stats.web_searches_week, l: "Web-поисков за неделю" },
              ].map((s, i) => (
                <div key={i} className="glass rounded-2xl p-4" data-testid={`stat-card-${i}`}>
                  <div className="font-heading text-3xl font-bold text-gold">{s.v}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-widest mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-8 flex-wrap">
          {visible.map((tt) => {
            const Icon = tt.icon;
            const active = tab === tt.id;
            return (
              <button key={tt.id} onClick={() => setTab(tt.id)}
                data-testid={`dashboard-${tt.id}-tab`}
                className={`px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 border transition-all ${active ? "" : "border-white/10 text-gray-300 hover:border-gold/50"}`}
                style={active ? {background: "var(--gold)", color: "#06060A", borderColor: "var(--gold)"} : {}}>
                <Icon size={14}/> {tt.label}
              </button>
            );
          })}
        </div>

        <TabContent tab={tab} user={user}/>
      </div>
    </div>
  );
}

const PANELS = {
  services: ServicesPanel, portfolio: PortfolioPanel, reviews: ReviewsPanel, users: UsersPanel,
  orders: OrdersPanel, calendar: CalendarPanel, rates: RatesPanel, settings: SettingsPanel,
};

function TabContent({ tab, user }) {
  if (tab === "logs") return <LogsPanel/>;
  const Panel = PANELS[tab];
  return Panel ? <Panel/> : <StubPanel tab={tab}/>;
}

const DESCR = {
  services: "CRUD по услугам: категория (E-COMMERCE / BRANDING / GAME ART / WEB / AI_SOLUTIONS / ENTERPRISE_AI / SUBSCRIPTION / TARGET), цена ₽, tiers, min/max коридор, иконка, порядок, переводы RU/EN/CN.",
  portfolio: "Загрузка фото (bulk до 10), автосжатие до 5 МБ, замена, порядок.",
  reviews: "Фильтры (все / опубл. / скрытые / на проверке), автопубликация, модерация.",
  users: "➕ Заказ, ✏️ Профиль, 📊 Статистика, ➕ Отзыв, 🏷️ Статус, Заблокировать, Написать письмо.",
  orders: "Фильтры по статусам, бриф, дедлайн, язык клиента (RU / EN / CN).",
  calendar: "Виды месяц / неделя / день. Личные, Дедлайны, Все.",
  market: "AI-агент: Конкуренты, Рекомендации по ценам, История, Автономия.",
  search: "История web-поисков AI: дата, вопрос, URL источника, релевантность.",
  roles: "Матрица прав: Владелец / Администратор / Модератор / Пользователь.",
  rates: "ЦБ РФ + fallback (exchangerate.host / open.er-api.com). Обновление раз в час.",
  settings: "Смена пароля, экспорт БД, напоминания, автономия AI, настройки web-поиска, язык по умолчанию.",
};

function StubPanel({ tab }) {
  return (
    <div className="glass rounded-2xl p-8" data-testid={`panel-${tab}`}>
      <div className="chip mb-3">Phase 1A · stub</div>
      <h3 className="font-heading text-2xl font-bold mb-2">{TABS.find((t) => t.id === tab)?.label}</h3>
      <p className="text-gray-300">{DESCR[tab] || "Раздел будет реализован в следующих этапах."}</p>
      <p className="text-xs text-gray-500 mt-4 font-mono">status: scheduled · see roadmap parts 2–20</p>
    </div>
  );
}

function LogsPanel() {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/logs"); setLogs(data.logs); }
      catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    })();
  }, []);
  return (
    <div className="glass rounded-2xl p-6" data-testid="panel-logs">
      <h3 className="font-heading text-2xl font-bold mb-4">📋 Журнал событий ({logs.length})</h3>
      {logs.length === 0 ? <p className="text-gray-400">Событий пока нет.</p> : (
        <div className="space-y-2">
          {logs.map((l) => (
            <div key={l.id} className="glass-cyan rounded-lg p-3 flex flex-wrap items-center gap-3 text-sm font-mono">
              <span className="text-cyan min-w-[160px]">{l.action}</span>
              <span className="text-gray-300 flex-1 min-w-0 truncate">{l.description}</span>
              <span className="text-gold text-xs">{l.user_name || "—"}</span>
              <span className="text-gray-500 text-xs">{l.ip}</span>
              <span className="text-gray-500 text-xs">{l.source}</span>
              <span className="text-gray-500 text-xs">{l.created_at?.slice(0, 19)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
